import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  User,
  Mail,
  Building,
  Calendar,
  TrendingUp,
  Presentation,
  Eye,
  Trophy,
  Target,
  Play,
  Video
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserProfile() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      const [userRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/sessions?user_id=${userId}`)
      ]);
      setUser(userRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#059669]";
    if (score >= 60) return "text-[#2563EB]";
    return "text-[#D97706]";
  };

  // Generate chart data from sessions
  const chartData = sessions
    .filter(s => s.overall_score)
    .slice(-10)
    .map((s, idx) => ({
      name: `S${idx + 1}`,
      score: s.overall_score,
      topic: s.topic_name
    }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-[#5C5C5C]">User not found</p>
        <Link to="/admin/users">
          <Button className="mt-4">Back to Users</Button>
        </Link>
      </div>
    );
  }

  const completedSessions = sessions.filter(s => s.status === 'analyzed');
  const avgScore = user.average_score || 0;
  const totalSessions = user.sessions_completed || 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/users">
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Profile
          </h1>
          <p className="text-[#5C5C5C] mt-1">Complete analysis and session history</p>
        </div>
      </div>

      {/* Profile Header Card */}
      <Card className="stats-card overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-primary to-primary/70" />
        <CardContent className="pt-0">
          <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-12">
            <Avatar className="w-24 h-24 border-4 border-white shadow-lg bg-primary text-white">
              <AvatarFallback className="bg-primary text-white text-2xl font-bold">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 pb-2">
              <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {user.name}
              </h2>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-[#5C5C5C]">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </span>
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {user.department}
                </span>
                <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className={`badge ${user.role === 'admin' ? 'badge-info' : 'badge-success'}`}>
                  {user.role}
                </span>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-center px-6 py-3 bg-primary/5 rounded-xl">
                <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {avgScore}
                </p>
                <p className="text-xs text-[#5C5C5C]">Avg Score</p>
              </div>
              <div className="text-center px-6 py-3 bg-accent/10 rounded-xl">
                <p className="text-3xl font-bold text-accent" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {totalSessions}
                </p>
                <p className="text-xs text-[#5C5C5C]">Sessions</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats and Chart Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Performance Stats */}
        <Card className="stats-card" data-testid="performance-stats">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Performance Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#5C5C5C]">Average Score</span>
                <span className={`font-semibold ${getScoreColor(avgScore)}`}>{avgScore}/100</span>
              </div>
              <Progress value={avgScore} className="h-2" />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="p-4 bg-secondary/50 rounded-xl text-center">
                <Presentation className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A1A1A]">{sessions.length}</p>
                <p className="text-xs text-[#5C5C5C]">Total Sessions</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-xl text-center">
                <Trophy className="w-6 h-6 text-accent mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#1A1A1A]">{completedSessions.length}</p>
                <p className="text-xs text-[#5C5C5C]">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Trend Chart */}
        <Card className="stats-card" data-testid="score-trend">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Score Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} />
                    <YAxis domain={[0, 100]} stroke="#9CA3AF" fontSize={12} />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white p-3 rounded-lg shadow-lg border">
                              <p className="text-sm font-medium">{payload[0].payload.topic}</p>
                              <p className="text-lg font-bold text-primary">{payload[0].value}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#0F5156" 
                      strokeWidth={3}
                      dot={{ fill: '#0F5156', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[#5C5C5C]">
                No session data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Session History */}
      <Card className="stats-card" data-testid="session-history">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Presentation className="w-5 h-5 text-primary" />
            Session History ({sessions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Topic</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => (
                    <tr key={session.id} data-testid={`session-row-${index}`}>
                      <td>
                        <div>
                          <p className="font-medium">{session.topic_name}</p>
                          <p className="text-sm text-[#5C5C5C] truncate max-w-xs">
                            {session.concept_overview}
                          </p>
                        </div>
                      </td>
                      <td className="text-[#5C5C5C]">
                        {new Date(session.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </td>
                      <td>
                        <span className={`badge ${
                          session.status === 'analyzed' ? 'badge-success' :
                          session.status === 'completed' ? 'badge-info' :
                          'badge-warning'
                        }`}>
                          {session.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        {session.overall_score ? (
                          <span className={`font-bold ${getScoreColor(session.overall_score)}`}>
                            {session.overall_score}
                          </span>
                        ) : (
                          <span className="text-[#9CA3AF]">-</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {session.status === 'analyzed' && (
                            <>
                              <Link to={`/session/${session.id}/replay`}>
                                <Button variant="ghost" size="sm" className="rounded-full" data-testid={`watch-btn-${index}`}>
                                  <Play className="w-4 h-4 mr-1" />
                                  Watch
                                </Button>
                              </Link>
                              <Link to={`/session/${session.id}/feedback`}>
                                <Button variant="ghost" size="sm" className="rounded-full">
                                  <Eye className="w-4 h-4 mr-1" />
                                  Feedback
                                </Button>
                              </Link>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state py-12">
              <Presentation className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-[#5C5C5C]">No sessions yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
